import React, { useState, useEffect } from 'react'
import { CommonContainer } from '@components'
import { Grid, TextField, Typography, Button, Box, Tabs, Tab } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { apiAddHedgeMargin } from '@store'
import { showToast } from '@utils'
import AutoPricingLimits from './AutoPricingLimits'
import EexUserLimits from '../../gas/eex/EexUserLimits'

const implementedConditions = [
    "When volume on any contract is less than",
    "When Months are more than",
    "When Quarter is more than",
    "When seasons are more than",
    "When Calendars are more than",
]

const HedgingMargin = () => {
    const dispatch = useDispatch()
    const { hedgeMargin, isGetHedgeMarginLoading } = useSelector((state) => state.hedgeMargin)

    const [value, setValue] = useState(0)

    const handleTabChange = (event, newValue) => {
        setValue(newValue)
    }

    return (
        <CommonContainer>
            <Box sx={{ width: '100%' }}>
                <Box
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}
                >
                    <Tabs
                        value={value}
                        onChange={handleTabChange}
                        aria-label="Hedging margin navigation tabs"
                        role="navigation"
                        sx={{
                            background: '#fff',
                            color: 'white',
                            borderRadius: '5px',
                            padding: '5px',
                            margin: '0 0 10px 10px',
                            boxShadow: '0 0 5px rgba(0, 0, 0, 0.1)',
                            minHeight: '45px',
                            '& .MuiTabs-indicator': {
                                display: 'none'
                            },
                            '& .MuiTab-root': {
                                minHeight: '35px',
                                height: '35px',
                                '&.Mui-selected': {
                                    color: '#3AACFE',
                                    background: '#DFF2FF',
                                    borderRadius: '5px',
                                    borderBottom: 'none'
                                }
                            }
                        }}
                    >
                        <Tab label="Auto Pricing Margins" />
                        <Tab label="EEX User Limits" />
                        <Tab label="Hedging Margins" />
                    </Tabs>
                </Box>
                <Box sx={{ padding: '10px' }}>
                    {value === 0 && <AutoPricingLimits />}
                    {value === 1 && <EexUserLimits />}
                    {value === 2 && (
                        <HedgingMarginsContent
                            dispatch={dispatch}
                            hedgeMargin={hedgeMargin}
                            isGetHedgeMarginLoading={isGetHedgeMarginLoading}
                        />
                    )}
                </Box>
            </Box>
        </CommonContainer>
    )
}

const HedgingMarginsContent = ({ dispatch, hedgeMargin, isGetHedgeMarginLoading }) => {
    const [state, setState] = useState(initialState);

    useEffect(() => {
        if (hedgeMargin?.length > 0) {
            const newState = hedgeMargin?.filter((item) => implementedConditions?.includes(item?.condition))?.map((item, index) => {
                return {
                    Condition: item?.condition,
                    MW: item?.mw,
                    Margin: item?.margin
                }
            })
            setState(newState)
        }
    }, [hedgeMargin])

    const handleChange = (e, index) => {
        const { name, value } = e.target
        const list = [...state]
        list[index][name] = value
        setState(list)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        const data = state?.map((item) => {
            return {
                Condition: item?.Condition,
                MW: parseInt(item?.MW) || 0,
                Margin: parseFloat(item?.Margin) || 0
            }
        })

        const resp = await dispatch(apiAddHedgeMargin(data))
        if (resp?.meta?.requestStatus === 'fulfilled') {
            showToast('success', 'Margin added successfully')
        } else if (resp?.meta?.requestStatus === 'rejected') {
            showToast('error', resp.error.message)
        }
    }

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <Typography variant="h6">Conditions Margin</Typography>
                </Grid>
            </Grid>
            {isGetHedgeMarginLoading ?
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    Loading...
                </Box> :
                <>
                    {state?.map((item, index) => (
                        <Grid container spacing={2} sx={{ alignItems: 'center' }} mb={1} key={index}>
                            <Grid item xs={6}>
                                <Typography
                                    color={'black'}
                                    value={item?.Condition}
                                >
                                    {item?.Condition}
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    type='number'
                                    label="MW"
                                    variant="outlined"
                                    onKeyDown={(e) => e.key === 'e' && e.preventDefault()}
                                    name='MW'
                                    value={item?.MW}
                                    onChange={(e) => handleChange(e, index)}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    type='number'
                                    label="Margin"
                                    variant="outlined"
                                    onKeyDown={(e) => e.key === 'e' && e.preventDefault()}
                                    name='Margin'
                                    value={item?.Margin}
                                    onChange={(e) => handleChange(e, index)}
                                />
                            </Grid>
                        </Grid>
                    ))}
                </>}
            < Box sx={{ display: 'flex', justifyContent: 'flex-end', marginRight: 4 }}>
                <Button
                    variant="contained"
                    className='page-button'
                    sx={{
                        bgcolor: '#0b9cfd',
                        '&:hover': {
                            bgcolor: '#0b9cfd',
                        },
                    }}
                    onClick={handleSubmit}
                >
                    Submit
                </Button>
            </Box>
        </>
    )
}

const initialState = [
    {
        Condition: "When volume on any contract is less than",
        MW: "",
        Margin: ""
    },
    // {
    //     Condition: "When Spot is more than",
    //     MW: "",
    //     Margin: ""
    // },
    // {
    //     Condition: "For every spot interval",
    //     MW: "",
    //     Margin: ""
    // },
    {
        Condition: "When Months are more than",
        MW: "",
        Margin: ""
    },
    // {
    //     Condition: "For every Months interval",
    //     MW: "",
    //     Margin: ""
    // },
    {
        Condition: "When Quarter is more than",
        MW: "",
        Margin: ""
    },
    // {
    //     Condition: "For every Quarter interval",
    //     MW: "",
    //     Margin: ""
    // },
    {
        Condition: "When seasons are more than",
        MW: "",
        Margin: ""
    },
    // {
    //     Condition: "For every seasons interval",
    //     MW: "",
    //     Margin: ""
    // },
    {
        Condition: "When Calendars are more than",
        MW: "",
        Margin: ""
    },
    // {
    //     Condition: "For every Calendars interval",
    //     MW: "",
    //     Margin: ""
    // }
]

export default HedgingMargin